import React, { createContext, Dispatch, FC, ReactNode, useContext, useEffect, useState, useCallback } from 'react';
import Cookies from 'js-cookie';
import useFetchWithAuth from '../../../CommonHooks/useFetchWithAuth';
import { errorToast } from '../../../CommonComponent/toast';
import { useMainContainerProvider } from '../../../mainContainerProvider';
import { noticeBaseUrl } from '../../../baseUrl';
import { employeeDetailsValidation } from '../utils';
import { Employee, InstituteProfile, StudentProfile } from '../types';
import { UserRole } from '../../../globalContant';
import { noop } from 'lodash';

export interface ErrorStateType {
  field: string;
  message: string;
}
interface InstituteDetailContextType {
  isDetailReadOnly: boolean;
  setIsDetailsReadOnly: Dispatch<React.SetStateAction<boolean>>;
  profileData?: InstituteProfile | StudentProfile | Employee;
  localProfileData?: InstituteProfile | StudentProfile | Employee;
  setLocalProfileData: Dispatch<React.SetStateAction<InstituteProfile | StudentProfile | Employee>>;
  handleUpdate: () => void;
  isLoading: boolean;
  userType: string | undefined;
  setFile: Dispatch<React.SetStateAction<File | null>>;
  setMotherFile: Dispatch<React.SetStateAction<File | null>>;
  setFatherFile: Dispatch<React.SetStateAction<File | null>>;
  file: File | null;
  motherFile: File | null;
  fatherFile: File | null;
  setErrorState: Dispatch<React.SetStateAction<ErrorStateType[]>>;
  errorState: ErrorStateType[];
}

const initialValue: InstituteDetailContextType = {
  isDetailReadOnly: true,
  setIsDetailsReadOnly: noop,
  profileData: undefined,
  localProfileData: undefined,
  setLocalProfileData: noop,
  handleUpdate: noop,
  isLoading: false,
  userType: '',
  setFile: noop,
  setMotherFile: noop,
  setFatherFile: noop,
  file: null,
  motherFile: null,
  fatherFile: null,
  setErrorState: noop,
  errorState: [],
};

const InstituteProfileContext = createContext<InstituteDetailContextType>(initialValue);

const InstituteProfileProvider: FC<{ children: ReactNode }> = ({ children }) => {
  const [isDetailReadOnly, setIsDetailsReadOnly] = useState(true);
  const [profileData, setProfileData] = useState<InstituteProfile | StudentProfile | Employee>({});
  const [localProfileData, setLocalProfileData] = useState<InstituteProfile | StudentProfile | Employee>({});
  const [isLoading, setIsLoading] = useState(false);
  const [file, setFile] = useState<File | null>(null);
  const [motherFile, setMotherFile] = useState<File | null>(null);
  const [fatherFile, setFatherFile] = useState<File | null>(null);
  const [errorState, setErrorState] = useState<ErrorStateType[]>([]);

  const uid = Cookies.get('uid');
  const userType = Cookies.get('userType');
  const { fetchWithAuth } = useFetchWithAuth();
  const { getUpdatedClass } = useMainContainerProvider();

  const handleUpload = useCallback(
    async (file: File): Promise<string | undefined> => {
      if (!file) return;

      const formData = new FormData();
      formData.append('name', new Date().toISOString());
      formData.append('file', file);
      formData.append('public', 'true');
      const { error, data } = await fetchWithAuth('document/', { method: 'POST', body: formData }, noticeBaseUrl);
      if (error) {
        console.error('Upload failed:', error);
        return;
      }
      return data?.id;
    },
    [fetchWithAuth]
  );

  const handleUpdate = useCallback(async () => {
    if (!localProfileData) {
      errorToast('Profile details are missing');
      return;
    }

    if (userType === UserRole.STUDENT && 'enableModification' in localProfileData) {
      // eslint-disable-next-line
      (localProfileData as any).enableModification = false;
    }

    if (userType === '2') {
      const validationError = employeeDetailsValidation(localProfileData as Employee);
      if (validationError) {
        errorToast(validationError);
        return;
      }
    }

    setIsLoading(true);

    try {
      const [profileImageId, motherProfileImageId, fatherProfileImageId] = await Promise.all([
        file ? handleUpload(file) : Promise.resolve(''),
        motherFile ? handleUpload(motherFile) : Promise.resolve(''),
        fatherFile ? handleUpload(fatherFile) : Promise.resolve(''),
      ]);

      // 🔚 Check if any file upload failed
      if ((file && !profileImageId) || (motherFile && !motherProfileImageId) || (fatherFile && !fatherProfileImageId)) {
        errorToast('Failed to upload one or more documents. Please try again.');
        setIsLoading(false);
        return;
      }
      // eslint-disable-next-line
      const { [Object.keys(localProfileData)[0]]: _, ...payload } = localProfileData as Record<string, any>;

      const requestBody = {
        ...payload,
        uid,
        userType,
        ...(profileImageId && { profileDocRefId: profileImageId }),
        ...(motherProfileImageId && { motherProfileDocRefId: motherProfileImageId }),
        ...(fatherProfileImageId && { fatherProfileDocRefId: fatherProfileImageId }),
      };

      const { error, data } = await fetchWithAuth('profile/', {
        method: 'POST',
        body: JSON.stringify(requestBody),
      });

      if (error) {
        errorToast(error);
        return;
      }

      const newProfile = data?.data[0];
      setProfileData(newProfile);
      setLocalProfileData(newProfile);
      if (userType === UserRole.ADMIN) {
        Cookies.set('instituteLogo', newProfile.profileDocFile, { expires: 1 });
      }
      if (userType !== UserRole.ADMIN) {
        Cookies.set('profileLogo', newProfile.profileDocFile, { expires: 1 });
      }
      getUpdatedClass();
      setIsDetailsReadOnly(true);
      setFile(null);
      setMotherFile(null);
      setFatherFile(null);
    } catch (e) {
      console.error('Unexpected error during profile update:', e);
      errorToast('Something went wrong while updating the profile.');
    } finally {
      setIsLoading(false);
    }
    // eslint-disable-next-line
  }, [localProfileData, userType, file, motherFile, fatherFile, fetchWithAuth, uid, getUpdatedClass]);

  const handleFetchProfile = useCallback(async () => {
    setIsLoading(true);
    const { error, data } = await fetchWithAuth(`profile/?uid=${uid}&userType=${userType}`);
    setIsLoading(false);

    if (error) {
      errorToast(error);
      return;
    }

    const response: InstituteProfile[] = data;
    const profile = response[0];
    setProfileData(profile);
    setLocalProfileData(profile);
  }, [fetchWithAuth, uid, userType]);

  useEffect(() => {
    if (uid && userType) {
      handleFetchProfile();
    }
  }, [uid, userType, handleFetchProfile]);

  return (
    <InstituteProfileContext.Provider
      value={{
        isDetailReadOnly,
        setIsDetailsReadOnly,
        profileData,
        localProfileData,
        setLocalProfileData,
        handleUpdate,
        isLoading,
        userType,
        setFile,
        setMotherFile,
        setFatherFile,
        file,
        motherFile,
        fatherFile,
        setErrorState,
        errorState,
      }}
    >
      {children}
    </InstituteProfileContext.Provider>
  );
};

export const useInstituteProvider = () => useContext(InstituteProfileContext);

export default InstituteProfileProvider;
